import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Filter, QUESTION_SEARCH_MAX_LENGTH } from 'cat-shared/utils'
import { BrowseQuestionsFormValues } from 'client/utils'
import { usePageStateContext } from 'client/hooks'
import { QuestionCategorySelect } from 'client/components'

import {
  AppButton,
  AppGroup,
  AppFormInput,
  AppForm,
  AppFormSelect,
  AppDivider,
} from 'client/ui-building-blocks'

type Props = {
  activePage: number
  totalPages: number
  isLoading: boolean
  disabled: boolean
  onPageChange: (pageNo: number) => void
  onSubmitClick: (data: BrowseQuestionsFormValues) => void
}

const SearchForm = ({
  activePage,
  totalPages,
  isLoading = false,
  disabled = false,
  onPageChange,
  onSubmitClick,
}: Props) => {
  const { t } = useTranslation()

  const { setBrowseQuestionsPageState } = usePageStateContext()

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    setValue,
  } = useFormContext<BrowseQuestionsFormValues>()

  const onSearchClearClick = () => {
    setValue('search', '', { shouldDirty: true })
    setBrowseQuestionsPageState(state => ({ ...state, search: '' }))
  }

  const onResetClick = () => {
    setValue('filter', Filter.Answered, { shouldDirty: true })
    setValue('category', 'any', { shouldDirty: true })
    setValue('search', '', { shouldDirty: true })
    setBrowseQuestionsPageState(state => ({
      ...state,
      filter: Filter.Answered,
      category: 'any',
      search: '',
    }))
  }

  const updatePageStateOnChange = (fieldName: string, fieldValue: string) => {
    setBrowseQuestionsPageState(state => ({
      ...state,
      [fieldName]: fieldValue,
    }))
  }

  const FILTER_OPTIONS = useMemo(() => {
    const labels = t('browseQuestionsPage.fields.filter.options', { returnObjects: true })
    return [
      { value: Filter.Answered, text: labels[Filter.Answered] },
      { value: Filter.Created, text: labels[Filter.Created] },
      { value: Filter.Followed, text: labels[Filter.Followed] },
    ]
  }, [t])

  return (
    <AppForm
      onSubmit={handleSubmit(data => {
        return new Promise(resolve => {
          if (!isLoading) {
            onSubmitClick(data)
          } else {
            resolve(true)
          }
        })
      })}
    >
      <AppGroup fullWidth alignment="verticalMobile" gapSize="small">
        <AppGroup alignment="verticalMobile" gapSize="small">
          <Controller
            control={control}
            name="filter"
            render={({ field: { value, onChange } }) => {
              return (
                <AppFormSelect
                  label={t('browseQuestionsPage.fields.filter.label')}
                  value={value}
                  options={FILTER_OPTIONS}
                  onChange={e => {
                    updatePageStateOnChange('filter', e.target.value as string)
                    onChange(e)
                  }}
                  disabled={disabled || isLoading || FILTER_OPTIONS.length === 1}
                />
              )
            }}
          />
          <QuestionCategorySelect
            includeAnyCategory
            isDisabled={disabled || isLoading}
            onChange={e => {
              updatePageStateOnChange('category', e.target.value as string)
            }}
          />
        </AppGroup>
        <Controller
          control={control}
          name="search"
          render={({ field: { value, onChange } }) => {
            return (
              <AppFormInput
                value={value}
                onChange={e => {
                  updatePageStateOnChange('search', e.target.value as string)
                  onChange(e)
                }}
                label={t('browseQuestionsPage.fields.search.label')}
                placeholder={t('browseQuestionsPage.fields.search.placeholder')}
                maxLength={QUESTION_SEARCH_MAX_LENGTH}
                onClearText={onSearchClearClick}
                disabled={disabled || isLoading}
              />
            )
          }}
        />
      </AppGroup>
      <AppGroup fullWidth="mobile" alignment="verticalMobile">
        <AppButton
          id="search-form-submit-btn"
          variant="info"
          text={t('common.buttons.search')}
          loading={isLoading}
          disabled={disabled}
        />
        <AppButton
          variant="blank"
          text={t('common.buttons.reset')}
          type="reset"
          onClick={onResetClick}
          disabled={!isDirty || isLoading}
        />
      </AppGroup>
      <AppDivider border size="xxsmall" />
      <AppGroup fullWidth="mobile" alignment="verticalMobile" gapSize="small">
        <AppFormSelect
          options={Array.from({ length: totalPages }).map((value, index) => ({
            value: String(index + 1),
            text: t('common.list.pagination.pageNo', { pageNo: index + 1 }),
          }))}
          value={String(activePage)}
          onChange={e => onPageChange(Number(e.target.value))}
          disabled={isLoading || totalPages === 1}
        />
        <AppGroup gapSize="xxsmall">
          <AppButton
            variant="success"
            iconName="ANGLE_DOUBLE_LEFT"
            onClick={() => onPageChange(1)}
            disabled={isLoading || activePage === 1}
          />
          <AppButton
            variant="success"
            iconName="CHEVRON_LEFT"
            onClick={() => onPageChange(activePage - 1)}
            disabled={isLoading || activePage === 1}
          />
          <AppButton
            variant="success"
            iconName="CHEVRON_RIGHT"
            onClick={() => onPageChange(activePage + 1)}
            disabled={isLoading || activePage === totalPages}
          />
          <AppButton
            variant="success"
            iconName="ANGLE_DOUBLE_RIGHT"
            onClick={() => onPageChange(totalPages)}
            disabled={isLoading || activePage === totalPages}
          />
        </AppGroup>
      </AppGroup>
    </AppForm>
  )
}

export default SearchForm
